import type { operations } from "./generated/schema";
import { normalizeApiError, type ApiError } from "./errors";

export type AdminUploadResponse =
  operations["uploadAdminProductFile"]["responses"][201]["content"]["application/json"];

export interface AdminUploadProgress {
  readonly loaded: number;
  readonly total: number;
  readonly percent: number;
}

export interface AdminUploadOptions {
  readonly baseUrl: string;
  readonly productId: string;
  readonly formData: FormData;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: AdminUploadProgress) => void;
}

export interface AdminMultipartUploadOptions {
  readonly baseUrl: string;
  readonly endpoint: string;
  readonly formData: FormData;
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: AdminUploadProgress) => void;
}

export class AdminUploadError extends Error {
  readonly status: number;
  readonly apiError: ApiError;

  constructor(status: number, apiError: ApiError) {
    super(apiError.message);
    this.name = "AdminUploadError";
    this.status = status;
    this.apiError = apiError;
  }
}

function responsePayload(xhr: XMLHttpRequest): unknown {
  if (xhr.responseText.trim() === "") return undefined;
  try {
    return JSON.parse(xhr.responseText) as unknown;
  } catch {
    return undefined;
  }
}

function responseError(xhr: XMLHttpRequest): ApiError {
  const normalized = normalizeApiError(responsePayload(xhr));
  if (normalized.correlationId !== null) return normalized;
  return {
    ...normalized,
    correlationId: xhr.getResponseHeader("x-correlation-id")?.trim() || null,
  };
}

function abortedError(): AdminUploadError {
  return new AdminUploadError(0, {
    code: "UPLOAD_ABORTED",
    message: "O upload foi cancelado.",
    correlationId: null,
    fieldErrors: {},
  });
}

export function adminPreviewUrl(baseUrl: string, storedFileId: string): string {
  const normalizedBaseUrl = baseUrl.replace(/\/+$/u, "");
  return `${normalizedBaseUrl}/admin/catalog/images/${encodeURIComponent(storedFileId)}/preview`;
}

export function uploadAdminMultipart<T>(
  options: AdminMultipartUploadOptions,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const cleanup = () => {
      options.signal?.removeEventListener("abort", abortRequest);
    };
    const rejectWith = (error: AdminUploadError) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const abortRequest = () => {
      if (settled) return;
      rejectWith(abortedError());
      xhr.abort();
    };

    xhr.open("POST", `${options.baseUrl.replace(/\/+$/u, "")}${options.endpoint}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Accept", "application/json");
    xhr.upload.addEventListener("progress", (event) => {
      const total = event.total > 0 ? event.total : 0;
      const percent = total === 0
        ? 0
        : Math.min(100, Math.max(0, Math.round((event.loaded / total) * 100)));
      options.onProgress?.({ loaded: event.loaded, total, percent });
    });
    xhr.addEventListener("load", () => {
      if (settled) return;
      if (xhr.status >= 200 && xhr.status < 300) {
        const payload = responsePayload(xhr);
        if (payload === undefined) {
          rejectWith(new AdminUploadError(xhr.status, normalizeApiError(payload)));
          return;
        }
        settled = true;
        cleanup();
        resolve(payload as T);
        return;
      }
      rejectWith(new AdminUploadError(xhr.status, responseError(xhr)));
    });
    xhr.addEventListener("error", () => {
      rejectWith(new AdminUploadError(0, normalizeApiError(new TypeError("upload failed"))));
    });
    xhr.addEventListener("timeout", () => {
      rejectWith(new AdminUploadError(0, normalizeApiError(new TypeError("upload timed out"))));
    });
    xhr.addEventListener("abort", () => {
      rejectWith(abortedError());
    });
    options.signal?.addEventListener("abort", abortRequest, { once: true });

    if (options.signal?.aborted) {
      abortRequest();
      return;
    }
    xhr.send(options.formData);
  });
}

export function uploadAdminProductFile(
  options: AdminUploadOptions,
): Promise<AdminUploadResponse> {
  return uploadAdminMultipart<AdminUploadResponse>({
    baseUrl: options.baseUrl,
    endpoint: `/admin/catalog/products/${encodeURIComponent(options.productId)}/files`,
    formData: options.formData,
    ...(options.signal ? { signal: options.signal } : {}),
    ...(options.onProgress ? { onProgress: options.onProgress } : {}),
  });
}
